<template>
  <!-- Backdrop -->
  <Teleport to="body">
    <div v-if="item" class="qty-overlay" @click.self="$emit('cancel')">
      <div class="qty-modal">

        <!-- Header -->
        <div class="qty-modal__header">
          <div class="qty-modal__title">{{ item.item?.name }}</div>
          <div class="qty-modal__sub">
            <template v-if="mode === 'unpack'">
              {{ $t('picking.packed') }}: <strong>{{ item.packed_quantity ?? 0 }}</strong>
              <span class="qty-modal__over-inline" :style="{ visibility: isOverCap ? 'visible' : 'hidden' }">
                {{ $t('picking.moreThanPacked') }}
              </span>
            </template>
            <template v-else-if="mode === 'checkout'">
              <template v-if="stockInfo">
                {{ $t('picking.available') }}: <strong :class="stockInfo.consumable_net_available <= 0 ? 'qty-modal__stock-zero' : ''">{{ stockInfo.consumable_net_available }}</strong>
                <span v-if="stockInfo.consumable_net_available <= 0" class="qty-modal__over-inline">{{ $t('picking.noStock') }}</span>
                <span v-else-if="isOverPlanned" class="qty-modal__over-inline">{{ $t('picking.exceedsStock') }}</span>
              </template>
            </template>
            <template v-else>
              {{ $t('picking.planned') }}: <strong>{{ item.planned_quantity }}</strong>
              <template v-if="initialValue != null">
                &nbsp;&mdash; {{ $t('picking.currentlyPacked', { qty: initialValue }) }}
              </template>
              <template v-else-if="(item.packed_quantity ?? 0) > 0">
                <span class="qty-modal__packed-so-far">&nbsp;&mdash; {{ $t('picking.alreadyPacked', { qty: item.packed_quantity }) }}</span>
              </template>
              <span class="qty-modal__over-inline" :style="{ visibility: isOverPlanned ? 'visible' : 'hidden' }">
                {{ $t('picking.moreThanPlanned') }}
              </span>
            </template>
          </div>
          <button class="qty-modal__close" @click="$emit('cancel')" :aria-label="$t('common.close')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Overcount confirmation (pack mode only) -->
        <div v-if="showConfirm" class="qty-modal__confirm">
          <div class="qty-modal__confirm-msg">
            <template v-if="mode === 'checkout'">
              {{ $t('picking.confirmOverStock', { entered: enteredValue, available: stockInfo?.consumable_net_available ?? 0 }) }}
            </template>
            <template v-else>
              {{ $t('picking.confirmOverPack', { entered: enteredValue, planned: item.planned_quantity }) }}
            </template>
          </div>
          <div class="qty-modal__confirm-actions">
            <button class="qty-btn qty-btn--cancel" @click="showConfirm = false">{{ $t('picking.goBack') }}</button>
            <button class="qty-btn qty-btn--confirm" @click="commitConfirmed">{{ $t('picking.yesConfirm') }}</button>
          </div>
        </div>

        <!-- Normal input -->
        <template v-else>
          <!-- Quick-tap suggestions -->
          <div class="qty-modal__suggestions">
            <button
              v-for="s in suggestions"
              :key="s"
              class="qty-suggestion"
              :class="{ 'qty-suggestion--active': enteredValue === s }"
              @click="setQty(s)"
            >{{ s }}</button>
          </div>

          <!-- Large numeric display -->
          <div class="qty-modal__display" :class="{ 'qty-modal__display--over': isOverPlanned }">
            {{ enteredValue || '0' }}
          </div>

          <!-- Numpad -->
          <div class="qty-numpad">
            <button v-for="key in numpadKeys" :key="key" class="numpad-key" @click="numpadPress(key)">
              <template v-if="key === 'del'">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"/>
                  <line x1="18" y1="9" x2="12" y2="15"/><line x1="12" y1="9" x2="18" y2="15"/>
                </svg>
              </template>
              <template v-else>{{ key }}</template>
            </button>
          </div>

          <!-- Confirm button -->
          <button
            class="qty-btn qty-btn--full"
            :class="mode === 'unpack' ? 'qty-btn--unpack' : ((!enteredValue || enteredValue === '0') && initialValue != null ? 'qty-btn--unpack' : 'qty-btn--confirm')"
            :disabled="(initialValue == null && (!enteredValue || enteredValue === '0')) || (mode === 'unpack' && (!enteredValue || enteredValue === '0')) || (mode === 'checkout' && stockInfo != null && stockInfo.consumable_net_available <= 0)"
            @click="onConfirmClick"
          >
            <template v-if="mode === 'unpack'">{{ $t('picking.unpackQty', { qty: enteredValue || 0 }) }}</template>
            <template v-else-if="mode === 'checkout'">{{ $t('picking.addQty', { qty: enteredValue || 0 }) }}</template>
            <template v-else-if="initialValue != null && (!enteredValue || enteredValue === '0')">{{ $t('picking.removeFromList') }}</template>
            <template v-else-if="initialValue != null">{{ $t('picking.updateTo', { qty: enteredValue }) }}</template>
            <template v-else>{{ $t('picking.packQty', { qty: enteredValue || 0 }) }}</template>
          </button>
        </template>

      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  item:         { type: Object, default: null },
  mode:         { type: String, default: 'pack' },   // 'pack' | 'unpack' | 'checkout'
  initialValue: { type: Number, default: null },      // pre-fill for update
  // checkout mode: optional stock info for consumables
  stockInfo:    { type: Object, default: null },       // { available_quantity, consumable_net_available }
})

const emit = defineEmits(['confirm', 'cancel'])

const enteredValue = ref('')
const showConfirm  = ref(false)

const numpadKeys = ['1','2','3','4','5','6','7','8','9','','0','del']

// Quick-tap suggestions: in pack mode based on planned_quantity, in unpack mode based on packed_quantity
const suggestions = computed(() => {
  if (props.mode === 'checkout') {
    const max = props.stockInfo?.consumable_net_available
    if (max && max > 0) {
      const set = new Set([1, max])
      if (max >= 10) set.add(Math.round(max / 2))
      if (max >= 25) set.add(Math.round(max / 4))
      set.add(Math.min(max, 10))
      return [...set].filter(n => n > 0).sort((a, b) => a - b).slice(0, 5)
    }
    return [1, 5, 10, 25, 50]
  }
  const p = props.mode === 'unpack'
    ? (props.item?.packed_quantity ?? 0)
    : (props.item?.planned_quantity ?? 0)
  if (!p) return [1, 5, 10, 25, 50]
  const set = new Set([p])
  if (p >= 10) set.add(Math.round(p / 2))
  if (p >= 25) set.add(Math.round(p / 4))
  if (props.mode === 'pack') set.add(p + 10)
  return [...set].filter(n => n > 0).sort((a, b) => a - b).slice(0, 5)
})

const isOverPlanned = computed(() => {
  const v = parseInt(enteredValue.value, 10)
  if (props.mode === 'checkout') {
    const max = props.stockInfo?.consumable_net_available
    return max != null && max > 0 && v > max
  }
  const p = props.item?.planned_quantity ?? 0
  return p > 0 && v > p * 1.2
})

const isOverCap = computed(() => {
  const v = parseInt(enteredValue.value, 10)
  const p = props.item?.packed_quantity ?? 0
  return p > 0 && v > p
})

function setQty(n) {
  enteredValue.value = String(n)
}

function numpadPress(key) {
  if (key === 'del') {
    enteredValue.value = enteredValue.value.slice(0, -1)
  } else if (key === '') {
    // no-op (blank key slot)
  } else {
    if (enteredValue.value === '0') enteredValue.value = key
    else enteredValue.value += key
    // cap at 9999
    if (parseInt(enteredValue.value, 10) > 9999) enteredValue.value = '9999'
  }
}

function onConfirmClick() {
  const v = parseInt(enteredValue.value, 10) || 0
  // Allow 0 only in update mode (initialValue != null) — means "remove from list"
  if (v === 0 && props.initialValue == null) return
  if (v === 0 && props.mode === 'unpack') return
  if (props.mode === 'pack' && isOverPlanned.value) {
    showConfirm.value = true
  } else if (props.mode === 'checkout' && isOverPlanned.value) {
    // Over available stock — ask for confirmation
    showConfirm.value = true
  } else {
    emit('confirm', { item: props.item, quantity: v })
    reset()
  }
}

function commitConfirmed() {
  const v = parseInt(enteredValue.value, 10) || 0
  emit('confirm', { item: props.item, quantity: v })
  reset()
}

function reset() {
  enteredValue.value = ''
  showConfirm.value  = false
}

// Reset when item changes
watch(() => props.item, () => {
  reset()
  if (props.initialValue != null && props.initialValue > 0)
    enteredValue.value = String(props.initialValue)
})
</script>

<style scoped>
.qty-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  animation: fadeIn .15s ease;
}

@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }

.qty-modal {
  background: #111827;
  border: 1px solid #374151;
  border-radius: 1.25rem;
  padding: 1.25rem;
  width: 100%;
  max-width: 480px;
  animation: slideUp .2s ease;
}

@keyframes slideUp { from { transform: translateY(40px); opacity: 0 } to { transform: none; opacity: 1 } }

.qty-modal__header {
  position: relative;
  margin-bottom: 1rem;
}

.qty-modal__title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #f9fafb;
  padding-right: 2rem;
}

.qty-modal__sub { font-size: .875rem; color: #9ca3af; margin-top: .2rem; }

.qty-modal__close {
  position: absolute;
  top: 0; right: 0;
  background: none; border: none;
  color: #6b7280;
  cursor: pointer;
  padding: .25rem;
}
.qty-modal__close svg { width: 20px; height: 20px; }

.qty-modal__suggestions {
  display: flex;
  gap: .5rem;
  flex-wrap: wrap;
  margin-bottom: .875rem;
}

.qty-suggestion {
  flex: 1;
  min-width: 52px;
  padding: .5rem;
  background: #1f2937;
  border: 2px solid #374151;
  border-radius: .5rem;
  color: #d1d5db;
  font-size: .9rem;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  transition: border-color .1s, background .1s;
}

.qty-suggestion--active,
.qty-suggestion:hover {
  background: #1e3a5f;
  border-color: #3b82f6;
  color: #93c5fd;
}

.qty-modal__display {
  font-size: 3rem;
  font-weight: 800;
  text-align: center;
  color: #f9fafb;
  letter-spacing: .05em;
  line-height: 1;
  margin: .5rem 0 1rem;
  transition: color .15s;
}

.qty-modal__display--over { color: #fbbf24; }

.qty-numpad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: .5rem;
  margin-bottom: .75rem;
}

.numpad-key {
  padding: .875rem;
  background: #1f2937;
  border: 1px solid #374151;
  border-radius: .625rem;
  color: #f9fafb;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background .1s;
}
.numpad-key:hover  { background: #374151; }
.numpad-key:active { background: #4b5563; }
.numpad-key svg { width: 22px; height: 22px; }

.qty-modal__over-inline {
  font-size: .813rem;
  color: #fbbf24;
  font-weight: 600;
  margin-left: .25rem;
}

.qty-modal__stock-zero { color: #f87171; }

.qty-modal__packed-so-far {
  font-size: .813rem;
  color: #6ee7b7;
  font-weight: 500;
}

.qty-btn {
  padding: .875rem 1.5rem;
  border-radius: .625rem;
  border: none;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: background .1s;
}

.qty-btn--full { width: 100%; }

.qty-btn--confirm { background: #16a34a; color: #fff; }
.qty-btn--confirm:hover { background: #15803d; }
.qty-btn--confirm:disabled { background: #374151; color: #6b7280; cursor: not-allowed; }

.qty-btn--unpack { background: #7f1d1d; color: #fca5a5; }
.qty-btn--unpack:hover { background: #991b1b; }
.qty-btn--unpack:disabled { background: #374151; color: #6b7280; cursor: not-allowed; }

.qty-btn--cancel { background: #374151; color: #d1d5db; }
.qty-btn--cancel:hover { background: #4b5563; }

/* Confirm dialog */
.qty-modal__confirm { padding: 1rem 0; }
.qty-modal__confirm-msg {
  font-size: 1rem;
  color: #f9fafb;
  line-height: 1.5;
  margin-bottom: 1.25rem;
  text-align: center;
}
.qty-modal__confirm-actions { display: flex; gap: .75rem; }
.qty-modal__confirm-actions .qty-btn { flex: 1; }
</style>
